/**
 * Conversation Template Service
 *
 * CRUD for admin-owned conversation templates under
 * chartmindAdmins/{adminId}/conversationTemplates/{templateId}.
 *
 * Also resolves conversationTemplateRef (system defaults vs Firestore custom) for check-in.
 */

import { db } from './firebase';
import firebase from 'firebase/compat/app';
import { getDefaultConversationTemplateById } from '../constants/defaultConversationTemplates';

const MIN_CONVERSATION_QUESTIONS = 1;
const MAX_CONVERSATION_QUESTIONS = 60;
const DEFAULT_MAX_QUESTIONS = 20;

const ALLOWED_END_ACTIONS = new Set([
  'save_to_cmr',
  'notify_provider',
  'show_facility_map',
  'route_to_checkin',
]);

const ALLOWED_UPDATE_FIELDS = [
  'name',
  'title',
  'goals',
  'maxQuestions',
  'specialty',
  'touchpoints',
  'status',
  'basedOn',
  'endActions',
  'suggestedQuestions',
];

function normalizeEndActions(value) {
  if (!Array.isArray(value)) return [];
  return value.filter((item) => typeof item === 'string' && ALLOWED_END_ACTIONS.has(item));
}

function normalizeSuggestedQuestions(value) {
  if (!Array.isArray(value)) return [];
  return value.filter((q) => typeof q === 'string' && q.trim());
}

// ============================================================================
// COLLECTION HELPERS
// ============================================================================

function getTemplatesCollection(adminId) {
  return db
    .collection('chartmindAdmins')
    .doc(adminId)
    .collection('conversationTemplates');
}

/**
 * @param {{ touchpoints?: string[] }} template
 * @returns {string[]}
 */
export function normalizeTouchpoints(template) {
  if (template?.touchpoints?.length) {
    return template.touchpoints;
  }
  return ['securelink'];
}

/**
 * @param {Object} template
 * @param {{ source: string }} templateRef
 * @returns {Object}
 */
function normalizeResolvedTemplate(template, templateRef) {
  const base = template && typeof template === 'object' ? { ...template } : {};
  return {
    ...base,
    touchpoints: normalizeTouchpoints(base),
    source: templateRef.source,
  };
}

function assertAdminId(adminId) {
  const id = typeof adminId === 'string' ? adminId.trim() : '';
  if (!id) {
    throw new Error('adminId is required');
  }
  return id;
}

function clampMaxQuestions(n) {
  const num = typeof n === 'number' && !Number.isNaN(n) ? n : DEFAULT_MAX_QUESTIONS;
  return Math.min(MAX_CONVERSATION_QUESTIONS, Math.max(MIN_CONVERSATION_QUESTIONS, num));
}

function isNonArchivedStatus(docStatus) {
  if (docStatus === 'archived') return false;
  if (docStatus === undefined || docStatus === null || docStatus === '') return true;
  return docStatus === 'draft' || docStatus === 'active';
}

// ============================================================================
// READ
// ============================================================================

/**
 * List conversation templates for an admin with optional filters.
 *
 * When `status` is omitted, archived templates are excluded (client-side filter) so older
 * docs without `status` still appear.
 *
 * @param {string} adminId
 * @param {{ touchpoint?: string, status?: string, specialty?: string }} [filters]
 * @returns {Promise<Array<Object>>}
 */
export async function getConversationTemplates(adminId, filters = {}) {
  try {
    assertAdminId(adminId);
  } catch {
    return [];
  }

  const { touchpoint, status, specialty } = filters;

  try {
    let query = getTemplatesCollection(adminId);

    if (touchpoint) {
      query = query.where('touchpoints', 'array-contains', touchpoint);
    }
    if (specialty) {
      query = query.where('specialty', '==', specialty);
    }
    if (status !== undefined && status !== null && status !== '') {
      query = query.where('status', '==', status);
    }

    query = query.orderBy('updatedAt', 'desc');

    const snapshot = await query.get();
    let rows = snapshot.docs.map(doc => ({
      id: doc.id,
      templateId: doc.id,
      ...doc.data(),
    }));

    if (status === undefined || status === null || status === '') {
      rows = rows.filter(r => isNonArchivedStatus(r.status));
    }

    return rows;
  } catch (err) {
    console.warn('[conversationTemplateService] getConversationTemplates failed', err);
    return [];
  }
}

/**
 * @param {string} adminId
 * @param {string} templateId
 * @returns {Promise<Object|null>}
 */
export async function getConversationTemplate(adminId, templateId) {
  if (!adminId || !templateId) return null;

  const doc = await getTemplatesCollection(adminId).doc(templateId).get();
  if (!doc.exists) return null;

  return {
    id: doc.id,
    templateId: doc.id,
    ...doc.data(),
  };
}

// ============================================================================
// CREATE
// ============================================================================

/**
 * @param {string} adminId
 * @param {Object} data
 * @returns {Promise<Object>}
 */
export async function createConversationTemplate(adminId, data) {
  const aid = assertAdminId(adminId);

  if (!data?.name?.trim()) throw new Error('Template name is required');
  if (!data?.title?.trim()) throw new Error('Template title is required');
  if (!data?.goals?.trim()) throw new Error('Conversation goals are required');

  const maxQuestions = clampMaxQuestions(
    data.maxQuestions !== undefined ? Number(data.maxQuestions) : DEFAULT_MAX_QUESTIONS
  );

  const touchpoints = normalizeTouchpoints({ touchpoints: data.touchpoints });
  const specialty = typeof data.specialty === 'string' && data.specialty.trim()
    ? data.specialty.trim()
    : 'general';

  const docRef = getTemplatesCollection(aid).doc();
  const templateId = docRef.id;

  const payload = {
    templateId,
    adminId: aid,
    name: data.name.trim(),
    title: data.title.trim(),
    goals: data.goals.trim(),
    maxQuestions,
    specialty,
    touchpoints,
    endActions: normalizeEndActions(data.endActions),
    suggestedQuestions: normalizeSuggestedQuestions(data.suggestedQuestions),
    basedOn: data.basedOn != null ? data.basedOn : null,
    status: 'draft',
    createdAt: firebase.firestore.FieldValue.serverTimestamp(),
    updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
  };

  await docRef.set(payload);

  return {
    templateId,
    id: templateId,
    ...payload,
    createdAt: null,
    updatedAt: null,
  };
}

// ============================================================================
// UPDATE
// ============================================================================

/**
 * @param {string} adminId
 * @param {string} templateId
 * @param {Object} updates
 * @returns {Promise<void>}
 */
export async function updateConversationTemplate(adminId, templateId, updates) {
  if (!adminId || !templateId) throw new Error('adminId and templateId are required');

  const cleanUpdates = {};
  for (const key of Object.keys(updates || {})) {
    if (!ALLOWED_UPDATE_FIELDS.includes(key)) {
      console.warn('[conversationTemplateService] Ignoring unknown update field:', key);
      continue;
    }
    if (updates[key] === undefined) continue;

    if (key === 'name' || key === 'title' || key === 'goals') {
      cleanUpdates[key] = String(updates[key]).trim();
    } else if (key === 'maxQuestions') {
      cleanUpdates[key] = clampMaxQuestions(Number(updates[key]));
    } else if (key === 'touchpoints') {
      cleanUpdates[key] = normalizeTouchpoints({ touchpoints: updates[key] });
    } else if (key === 'endActions') {
      cleanUpdates[key] = normalizeEndActions(updates[key]);
    } else if (key === 'suggestedQuestions') {
      cleanUpdates[key] = normalizeSuggestedQuestions(updates[key]);
    } else {
      cleanUpdates[key] = updates[key];
    }
  }

  cleanUpdates.updatedAt = firebase.firestore.FieldValue.serverTimestamp();

  await getTemplatesCollection(adminId).doc(templateId).update(cleanUpdates);
}

// ============================================================================
// ARCHIVE / DELETE
// ============================================================================

/**
 * @param {string} adminId
 * @param {string} templateId
 * @returns {Promise<void>}
 */
export async function archiveConversationTemplate(adminId, templateId) {
  await updateConversationTemplate(adminId, templateId, { status: 'archived' });
}

/**
 * @param {string} adminId
 * @param {string} templateId
 * @returns {Promise<void>}
 */
export async function deleteConversationTemplate(adminId, templateId) {
  if (!adminId || !templateId) throw new Error('adminId and templateId are required');
  await getTemplatesCollection(adminId).doc(templateId).delete();
}

// ============================================================================
// FORK
// ============================================================================

/**
 * @param {string} adminId
 * @param {string} systemTemplateId
 * @returns {Promise<Object>}
 */
export async function forkSystemTemplate(adminId, systemTemplateId) {
  const original = getDefaultConversationTemplateById(systemTemplateId);
  if (!original) {
    throw new Error(`Unknown system template: ${systemTemplateId}`);
  }

  const touchpoints = normalizeTouchpoints(original);

  return createConversationTemplate(adminId, {
    name: `${original.title} (copy)`,
    title: original.title,
    goals: original.goals,
    maxQuestions: original.maxQuestions,
    specialty: original.specialty || 'general',
    touchpoints,
    basedOn: systemTemplateId,
  });
}

// ============================================================================
// RESOLVE
// ============================================================================

/**
 * @param {Object|null} ref
 * @param {string} ref.source - 'system' | 'custom'
 * @param {string} ref.templateId
 * @param {string} [ref.adminId]
 * @returns {Promise<Object|null>}
 */
export async function resolveConversationTemplate(ref) {
  if (!ref || !ref.source || !ref.templateId) return null;

  if (ref.source === 'system') {
    const raw = getDefaultConversationTemplateById(ref.templateId);
    if (!raw) return null;
    return normalizeResolvedTemplate(raw, ref);
  }

  if (ref.source === 'custom') {
    if (!ref.adminId) return null;
    const doc = await getConversationTemplate(ref.adminId, ref.templateId);
    if (!doc) return null;
    return normalizeResolvedTemplate(doc, ref);
  }

  return null;
}
